import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import { ApolloProvider, InMemoryCache } from '@apollo/client';
import type { MockedResponse } from '@apollo/client/testing';
import { MockedProvider } from '@apollo/client/testing';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';

import { createApolloClient } from '../../client';

interface QueryProviderProps {
  children: ReactNode;
  mocks?: MockedResponse[];
}

export const ApolloProviderWithProjectContext = ({ children }: { children: ReactNode }) => {
  const projectContext = useProjectContext({ optional: true });
  const projectId = projectContext?.project._id;

  const client = useMemo(() => createApolloClient(() => projectId), [projectId]);

  useEffect(
    () => () => {
      void client.stop();
    },
    [client],
  );

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export const QueryProvider = ({ children, mocks = [] }: QueryProviderProps) => {
  if (__TEST__) {
    // Silence Apollo 3.14 test noise: `no-cache` skips result normalization
    // (no "missing field" warnings for partial mocks, no cache.diff
    // canonizeResults deprecation), and passing our own cache avoids
    // MockedProvider's default `new InMemoryCache({ addTypename: true })`
    // deprecation notice.
    return (
      <MockedProvider
        mocks={mocks}
        cache={new InMemoryCache()}
        defaultOptions={{ watchQuery: { fetchPolicy: 'no-cache' } }}
      >
        {children}
      </MockedProvider>
    );
  }

  return <ApolloProviderWithProjectContext>{children}</ApolloProviderWithProjectContext>;
};
